import styled, { css } from 'styled-components';
import { Spinner } from '~/components/ui/Spinner';
import { Text } from '~/components/ui/Text';
import { menuItemShadow } from '../../assets/images';
import {
	TUNING_STUDIO_CARD_HEIGHT,
	TUNING_STUDIO_CARD_WIDTH,
} from './MenuItem.model';

const StyledShadow = styled.div`
	position: absolute;
	top: -190px;
	left: 50%;
	transform: translateX(-50%);
	width: 380px;
	height: 380px;
	background-image: url(${menuItemShadow});
	background-repeat: no-repeat;
	background-position: center;
	background-size: contain;
	transition: opacity 0.3s ease;
`;

const StyledMenuContainer = styled.div`
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 30px;

	${Text} {
		font-weight: 500;
		font-size: 18px;
		color: #ffffff;
		opacity: 0.5;
		text-align: center;
		white-space: nowrap;
		transition:
			opacity 0.3s ease,
			color 0.3s ease;
	}
`;

const StyledIconContainer = styled.div<{ $opacity?: number }>`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 60px;
	height: 60px;
	opacity: ${(props) => props.$opacity || 1};
`;

const StyledCount = styled.div`
	position: absolute;
	top: 20px;
	right: 20px;
	height: 20px;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 0 5px;
	border-radius: 5px;
	transition:
		background-color 0.3s ease,
		border 0.3s ease;

	${Text} {
		font-weight: 500;
		font-size: 12px;
		transition:
			opacity 0.3s ease,
			color 0.3s ease;
	}
`;

const StyledCountMenu = styled(StyledCount)`
	background-color: rgba(255, 255, 255, 0.02);
	border: 1px solid rgba(255, 255, 255, 0.05);

	${Text} {
		color: #ffffff;
		opacity: 0.5;
	}
`;

const StyledItemBadges = styled.div`
	position: absolute;
	top: 20px;
	right: 20px;
	display: flex;
	align-items: center;
	gap: 5px;
`;

const StyledCountItem = styled(StyledCount)`
	position: static;
	background-color: #ffffff;

	${Text} {
		color: #000000;
	}
`;

const StyledItemImage = styled.div`
	position: absolute;
	left: 50%;
	top: 50%;
	transform: translate(-50%, -50%);
	transition: opacity 0.3s ease;
`;

const StyledItemTop = styled.div`
	position: absolute;
	top: 20px;
	left: 20px;
	right: 20px;
	display: flex;
	align-items: center;
	justify-content: space-between;

	& > ${Text} {
		font-weight: 500;
		font-size: 16px;
		color: #ffffff;
		transition: color 0.3s ease;
	}
`;

const StyledPriceContainer = styled.div`
	position: absolute;
	bottom: 20px;
	left: 20px;
	right: 20px;
	display: grid;
	grid-template-columns: 1fr auto 16px;
	align-items: center;
	gap: 10px;

	${Text} {
		color: #ffffff;
		font-size: 16px;
		transition: color 0.3s ease;
	}

	.currency-icon {
		background-color: #ffffff;
	}
`;

const StyledStockContainer = styled.div`
	position: absolute;
	bottom: 20px;
	left: 20px;
	right: 20px;
	display: flex;
	align-items: center;
	justify-content: space-between;

	${Text} {
		color: #ffffff;
		font-size: 16px;
		transition: color 0.3s ease;
	}
`;

const StyledInstalledContainer = styled(StyledStockContainer)`
	.img-icon {
		background-color: #ffffff;
		transition: background-color 0.3s ease;
	}
`;

const StyledArrowButton = styled.div`
	position: absolute;
	top: 50%;
	transform: translateY(-50%);
	width: 30px;
	height: 30px;
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: rgba(255, 255, 255, 0.05);
	border: 1px solid rgba(255, 255, 255, 0.1);
	border-radius: 50%;
	cursor: pointer;
	transition:
		background-color 0.3s ease,
		border-color 0.3s ease;

	.img-icon {
		background-color: #ffffff;
		transition: background-color 0.3s ease;
	}

	&:hover {
		background-color: #ffffff;

		.img-icon {
			background-color: #000000;
		}
	}

	&[data-disabled] {
		background-color: rgba(255, 255, 255, 0.02);
		border-color: rgba(255, 255, 255, 0.05);
		cursor: default;
		pointer-events: none;

		.img-icon {
			background-color: rgba(255, 255, 255, 0.25);
		}
	}
`;

const StyledContainer = styled.div<{
	$type: 'menu' | 'item';
	$active?: boolean;
	$width?: number;
	$unavailable?: boolean;
	$installed?: boolean;
}>`
	position: relative;
	width: ${(props) => props.$width ?? TUNING_STUDIO_CARD_WIDTH}px;
	min-width: ${(props) => props.$width ?? TUNING_STUDIO_CARD_WIDTH}px;
	height: ${TUNING_STUDIO_CARD_HEIGHT}px;
	background-color: ${(props) => (props.$active ? '#2D70DA' : '#111314')};
	border-radius: 10px;
	border: 1px solid rgba(255, 255, 255, 0.1);
	overflow: hidden;
	transition: background-color 0.3s ease;
	cursor: pointer;

	&:hover {
		background-color: #ffffff;

		${StyledShadow} {
			opacity: 0;
		}

		${StyledMenuContainer}, ${StyledItemImage} {
			.img-icon {
				background-color: #000000;
			}

			${Spinner} {
				color: #000000;
			}

			${Text} {
				color: #000000;
				opacity: 1;
			}
		}

		${StyledCountMenu} {
			background-color: #000000;
			border: 1px solid rgba(0, 0, 0, 1);

			${Text} {
				color: #ffffff;
				opacity: 1;
			}
		}

		${StyledCountItem} {
			background-color: #000000;

			${Text} {
				color: #ffffff;
			}
		}

		${StyledItemTop} {
			& > ${Text} {
				color: #000000;
			}
		}

		${StyledPriceContainer}, ${StyledStockContainer} {
			${Text} {
				color: #000000;
			}

			.currency-icon {
				background-color: #000000;
			}
		}

		${StyledInstalledContainer} {
			.img-icon {
				background-color: #000000;
			}
		}

		${StyledArrowButton} {
			background-color: rgba(0, 0, 0, 0.05);
			border-color: rgba(0, 0, 0, 0.1);

			.img-icon {
				background-color: #000000;
			}

			&:hover {
				background-color: #000000;
				border-color: #000000;

				.img-icon {
					background-color: #ffffff;
				}
			}

			&[data-disabled] {
				background-color: rgba(0, 0, 0, 0.02);
				border-color: rgba(0, 0, 0, 0.05);

				.img-icon {
					background-color: rgba(0, 0, 0, 0.25);
				}
			}
		}
	}

	${StyledShadow} {
		opacity: ${(props) => (props.$active ? 1 : 0.25)};
	}

	${({ $active }) =>
		$active &&
		css`
			${StyledMenuContainer}, ${StyledItemImage} {
				${Text} {
					opacity: 1;
				}
			}

			${StyledCountItem} {
				${Text} {
					font-weight: 700;
					color: #2d70da;
				}
			}
		`}

	${({ $unavailable, $installed }) =>
		($unavailable || $installed) &&
		css`
			${StyledItemImage} {
				opacity: 0.5;
			}

			${StyledItemTop} {
				& > ${Text} {
					opacity: 0.5;
				}
			}
		`}
`;

export {
	StyledArrowButton,
	StyledContainer,
	StyledCountItem,
	StyledCountMenu,
	StyledIconContainer,
	StyledInstalledContainer,
	StyledItemBadges,
	StyledItemImage,
	StyledItemTop,
	StyledMenuContainer,
	StyledPriceContainer,
	StyledShadow,
	StyledStockContainer,
};
